'use client'

import Image from 'next/image'
import { Breadcrumbs } from 'src/components/Breadcrumbs'
import DOMPurify from 'src/_helpers/dompurify'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getOrcidLabel, getOrcidUrl } from 'src/_helpers/orcid'
import Typography from 'src/components/design-system/Typography'
import OrcidIcon from 'public/icons/interface/orcid.svg'
import LocationIcon from 'public/icons/interface/location.svg'
import EmailIcon from 'public/icons/interface/email.svg'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { orderResourcesByUuid } from 'src/_helpers/orderResourcesByUuid'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'

const getAffiliations = (author) => {
  return orderResourcesByUuid(author?.affiliations, author?.affiliationOrder)
}

const getAddress = (author) => author?.address

const normalizeLine = (value, locale) => {
  if (!value) return null
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    return getPreferredTranslation(value, locale)?.title || value.title || value.name || value.fullName || null
  }

  return String(value).trim()
}

const normalizeLines = (value, locale) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.map((line) => normalizeLine(line, locale)).filter(Boolean)
  }
  return String(value)
    .split(/\r?\n|\|/)
    .map((line) => line.trim())
    .filter(Boolean)
}

const Header = ({ breadcrumbs, page, locale }) => {
  const translation = page?.translations?.[locale] || {}
  const title = page?.fullName
  const translate = useTranslateEntry()
  const subtitle = translate('T_ABOUT_AUTHOR')
  const lead = translation?.text ? DOMPurify.sanitize(translation.text) : null
  const alt = translation?.alt || title
  const thumb = page?.thumbs?.detail
  const thumbUrl = thumb ? getMediaUrl(thumb.url, thumb.version) : null

  const orcid = page?.orcid
  const affiliation = getAffiliations(page)
  const address = getAddress(page)
  const email = page?.email
  const resolvedAffiliations = affiliation.length ? affiliation : []

  const orcidLink = getOrcidUrl(orcid)
  const orcidLabel = getOrcidLabel(orcid)
  const affiliationLines = normalizeLines(resolvedAffiliations, locale)

  return (
    <header className="flex flex-col gap-64 d:px-40 px-16">
      <Breadcrumbs breadcrumbs={breadcrumbs} current={title} />
      <div className={`grid gap-44 ${thumbUrl ? 'd:grid-cols-[377px_minmax(0,1fr)] d:gap-x-80' : ''}`.trim()}>
        <div className={`flex flex-col gap-24 ${thumbUrl ? 'd:col-start-2 d:row-start-1' : ''}`.trim()}>
          {title && (
            <Typography variant="h1" color="text-dark-blue-dark-blue1">
              {title}
            </Typography>
          )}
          <div className="flex flex-col gap-24">
            {orcidLink && (
              <div className="flex items-center gap-8">
                <OrcidIcon aria-hidden />
                <a href={orcidLink} target="_blank" rel="noreferrer" className="group/orcid flex items-center gap-8">
                  <div className="h-20 w-px bg-dark-blue-dark-blue4" aria-hidden />
                  <Typography
                    variant="h4-semi"
                    className="underline decoration-1 underline-offset-2 group-hover/orcid:no-underline"
                  >
                    {orcidLabel}
                  </Typography>
                </a>
              </div>
            )}

            {affiliationLines.length > 0 && (
              <div className="flex flex-col gap-4">
                {affiliationLines.map((line, index) => (
                  <Typography key={`${line}-${index}`} variant="h4-semi">
                    {line}
                  </Typography>
                ))}
              </div>
            )}

            {(address || email) && (
              <div className="flex flex-col gap-8">
                {address && (
                  <div className="flex items-center gap-6">
                    <LocationIcon aria-hidden />
                    <Typography variant="h4-semi">{address}</Typography>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-6">
                    <EmailIcon aria-hidden />
                    <a href={`mailto:${email}`}>
                      <Typography variant="h4-semi">{email}</Typography>
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
          {subtitle && (
            <Typography variant="h2" color="text-dark-blue-dark-blue1">
              {subtitle}
            </Typography>
          )}
          {lead && (
            <Typography
              variant="subtitle2"
              component="div"
              color="text-dark-blue-dark-blue1"
              className="text-block-content xl:max-w-[950px] max-w-665"
              dangerouslySetInnerHTML={{ __html: lead }}
            />
          )}
        </div>

        {thumbUrl && (
          <Image
            className="order-first h-auto w-full max-w-[377px] d:col-start-1 d:row-start-1 d:self-start d:sticky d:top-146"
            src={thumbUrl}
            alt={alt || title}
            width={thumb.width}
            height={thumb.height}
            priority
            fetchPriority="high"
            sizes="(max-width: 1024px) 100vw, 377px"
          />
        )}
      </div>

      <hr className="h-px w-full border-0 bg-dark-blue-dark-blue1" />
    </header>
  )
}

export default Header
